import { CompositeMeterData } from '@models/meter-scans/composite/composite-meter-data';
import { EqualizationData } from '@models/meter-scans/composite/equalization-data';
import { ClassCast, ObjectHelper } from '@utilities/object-helper';


export class CompositeScan {
  @ClassCast(CompositeMeterData)
  meterData: CompositeMeterData = new CompositeMeterData();
  @ClassCast(EqualizationData)
  equalizationData: EqualizationData[] = [];

  /**
   * This is a static function intended to act similar to a constructor, returning a new instance
   * of the object using input parameters
   *
   * The purpose of this function is for mapping non standard values from the api into
   * our standard data model in the UI
   *
   * @param httpResponse
   */
  static getScanFromHttpResponse(httpResponse: any): CompositeScan {
    const response = httpResponse && typeof httpResponse === 'object' ? httpResponse : {};
    const meterResponse = response.meterData && typeof response.meterData === 'object' ? response.meterData : {};
    const asObjectArray = (value: any): any[] => Array.isArray(value)
      ? value.filter(item => item && typeof item === 'object' && !Array.isArray(item))
      : [];
    const ofdmaData = asObjectArray(meterResponse.ofdmaData).map(scan => ({
      ...scan,
      groupDelay: asObjectArray(scan.groupDelay),
      impulse: asObjectArray(scan.impulse),
      icfr: asObjectArray(scan.icfr),
      transmit: asObjectArray(scan.transmit),
      unwrappedPhase: asObjectArray(scan.unwrappedPhase),
      wrappedPhase: asObjectArray(scan.wrappedPhase),
      icfrTilt: asObjectArray(scan.icfrTilt),
      raw: scan.raw && typeof scan.raw === 'object'
        ? { ...scan.raw, iucProfile: asObjectArray(scan.raw.iucProfile) }
        : scan.raw
    }));
    const safeMeterResponse = {
      ...meterResponse,
      channelData: asObjectArray(meterResponse.channelData),
      downstreamData: asObjectArray(meterResponse.downstreamData),
      upstreamData: asObjectArray(meterResponse.upstreamData),
      spectrumBins: asObjectArray(meterResponse.spectrumBins),
      ofdmaData
    };
    const safeResponse = {
      ...response,
      meterData: safeMeterResponse,
      equalizationData: asObjectArray(response.equalizationData)
    };
    const scan = ObjectHelper.copyObject(safeResponse, CompositeScan);

    scan.meterData.channelData.forEach((channel, index) => {
      channel.frequency = safeMeterResponse.channelData[index]?.freq ?? channel.frequency;
    });
    scan.meterData.downstreamData.forEach((channel, index) => {
      channel.frequency = safeMeterResponse.downstreamData[index]?.freq ?? channel.frequency;
      channel.power = safeMeterResponse.downstreamData[index]?.pwr ?? channel.power;
    });
    scan.meterData.upstreamData.forEach((channel, index) => {
      channel.frequency = safeMeterResponse.upstreamData[index]?.freq ?? channel.frequency;
      channel.power = safeMeterResponse.upstreamData[index]?.pwr ?? channel.power;
    });
    scan.meterData.latitude = meterResponse.lat ?? scan.meterData.latitude;
    scan.meterData.longitude = meterResponse.lon ?? scan.meterData.longitude;
    scan.meterData.ofdmaData.forEach(ofdmaScan => ofdmaScan.generateInitialMetaData());
    return scan;
  }

}
